import { QueryClient, QueryKey } from '@tanstack/react-query';
import { CatalogSectionCount } from '../../../modules/catalogs/catalog.types';
import { ESProductCatalogSearch } from '../../../modules/catalogs/products/product-catalog-es.types';
import { getGetCatalogSectionsKey } from '../catalogs/catalogs.keys';
import { CatalogSectionsQueryData } from '../catalogs/catalogs.queries.types';
import {
  ApplyOptimisticMoveParams,
  ApplyOptimisticMoveResult,
  CacheSnapshot,
  CacheSnapshotEntry,
} from './catalogDragAndDropCache.types';
import { getCatalogProductsPageKeyPrefix } from './catalogProducts.keys';

interface LoadedPage {
  queryKey: QueryKey;
  page: number;
  data: ESProductCatalogSearch[];
}

interface RecomposedPage {
  queryKey: QueryKey;
  data: ESProductCatalogSearch[];
}

interface ResolveInsertIndexParams {
  items: ESProductCatalogSearch[];
  afterItemId: string | null;
  toSectionId: string;
}

interface RecomposePagesParams {
  loadedPages: LoadedPage[];
  movedItemId: string;
  toSectionId: string;
  afterItemId: string | null;
  isCrossSection: boolean;
}

interface ShiftSectionCountsParams {
  sections: CatalogSectionCount[];
  fromSectionId: string;
  toSectionId: string;
  keepsEmptySections: boolean;
}

const readLoadedPages = (queryClient: QueryClient, pageKeyPrefix: QueryKey): LoadedPage[] => {
  const entries = queryClient.getQueriesData<ESProductCatalogSearch[]>({ queryKey: pageKeyPrefix });
  const pageSegmentIndex = pageKeyPrefix.length;

  return entries
    .flatMap(([queryKey, data]) =>
      Array.isArray(data) ? [{ queryKey, page: Number(queryKey[pageSegmentIndex]), data }] : [],
    )
    .sort((first, second) => first.page - second.page);
};

const isContiguous = (loadedPages: LoadedPage[]): boolean =>
  loadedPages.every((loadedPage, index) => loadedPage.page === loadedPages[0].page + index);

const resolveInsertIndex = ({
  items,
  afterItemId,
  toSectionId,
}: ResolveInsertIndexParams): number | null => {
  if (afterItemId !== null) {
    const anchorIndex = items.findIndex((item) => item.id === afterItemId);
    return anchorIndex === -1 ? null : anchorIndex + 1;
  }

  const sectionStartIndex = items.findIndex((item) => item.catalogSection.id === toSectionId);
  return sectionStartIndex === -1 ? null : sectionStartIndex;
};

const recomposePages = ({
  loadedPages,
  movedItemId,
  toSectionId,
  afterItemId,
  isCrossSection,
}: RecomposePagesParams): RecomposedPage[] => {
  if (!loadedPages.length || !isContiguous(loadedPages)) {
    return [];
  }

  const pageSize = Math.max(...loadedPages.map((loadedPage) => loadedPage.data.length));
  const items = loadedPages.flatMap((loadedPage) => loadedPage.data);

  const movedIndex = items.findIndex((item) => item.id === movedItemId);
  if (movedIndex === -1) {
    return [];
  }

  const [movedItem] = items.splice(movedIndex, 1);
  const itemToInsert = isCrossSection
    ? { ...movedItem, catalogSection: { ...movedItem.catalogSection, id: toSectionId } }
    : movedItem;

  const insertIndex = resolveInsertIndex({ items, afterItemId, toSectionId });
  if (insertIndex === null) {
    return [];
  }

  items.splice(insertIndex, 0, itemToInsert);

  return loadedPages.map((loadedPage, index) => ({
    queryKey: loadedPage.queryKey,
    data: items.slice(index * pageSize, (index + 1) * pageSize),
  }));
};

const shiftSectionCounts = ({
  sections,
  fromSectionId,
  toSectionId,
  keepsEmptySections,
}: ShiftSectionCountsParams): CatalogSectionCount[] =>
  sections
    .map((section) => {
      if (section.catalogSectionId === fromSectionId) {
        return { ...section, itemsCount: section.itemsCount - 1 };
      }

      if (section.catalogSectionId === toSectionId) {
        return { ...section, itemsCount: section.itemsCount + 1 };
      }

      return section;
    })
    .filter((section) => keepsEmptySections || section.itemsCount > 0);

interface ApplySectionCountShiftParams {
  queryClient: QueryClient;
  sectionsKey: QueryKey;
  entries: CacheSnapshotEntry[];
  fromSectionId: string;
  toSectionId: string;
  keepsEmptySections: boolean;
}

const applySectionCountShift = ({
  queryClient,
  sectionsKey,
  entries,
  fromSectionId,
  toSectionId,
  keepsEmptySections,
}: ApplySectionCountShiftParams): void => {
  const sectionsData = queryClient.getQueryData<CatalogSectionsQueryData>(sectionsKey);
  entries.push({ queryKey: sectionsKey, data: sectionsData });

  if (!sectionsData?.sections) {
    return;
  }

  queryClient.setQueryData<CatalogSectionsQueryData>(sectionsKey, {
    ...sectionsData,
    sections: shiftSectionCounts({
      sections: sectionsData.sections,
      fromSectionId,
      toSectionId,
      keepsEmptySections,
    }),
  });
};

export const applyOptimisticMove = (
  params: ApplyOptimisticMoveParams,
): ApplyOptimisticMoveResult => {
  const {
    queryClient,
    teamId,
    catalogId,
    contextParams,
    sortParams,
    groupParams,
    filters,
    customerIds,
    gridDataInclude,
    movedItemId,
    fromSectionId,
    toSectionId,
    afterItemId,
  } = params;

  const isCrossSection = fromSectionId !== toSectionId;

  const pageKeyPrefix: QueryKey = [
    teamId,
    ...getCatalogProductsPageKeyPrefix({
      catalogId,
      contextParams,
      sortParams,
      groupParams,
      filters,
      customerIds,
      gridDataInclude,
    }),
  ];
  const sectionsKey: QueryKey = [
    teamId,
    ...getGetCatalogSectionsKey({
      id: catalogId,
      filters,
      customerIds,
      contextParams,
      groupParams,
    }),
  ];

  const loadedPages = readLoadedPages(queryClient, pageKeyPrefix);

  const recomposedPages = recomposePages({
    loadedPages,
    movedItemId,
    toSectionId,
    afterItemId,
    isCrossSection,
  });

  if (!recomposedPages.length) {
    return { snapshot: { queryClient, entries: [] }, didRecompose: false };
  }

  const entries: CacheSnapshotEntry[] = loadedPages.map((loadedPage) => ({
    queryKey: loadedPage.queryKey,
    data: loadedPage.data,
  }));

  for (const recomposedPage of recomposedPages) {
    queryClient.setQueryData(recomposedPage.queryKey, recomposedPage.data);
  }

  if (isCrossSection) {
    applySectionCountShift({
      queryClient,
      sectionsKey,
      entries,
      fromSectionId,
      toSectionId,
      keepsEmptySections: !contextParams.isAdminSeller,
    });
  }

  return { snapshot: { queryClient, entries }, didRecompose: true };
};

export const rollbackMove = (snapshot: CacheSnapshot): void => {
  for (const entry of snapshot.entries) {
    snapshot.queryClient.setQueryData(entry.queryKey, entry.data);
  }
};
